import isUrl from 'is-url';
import React, {useCallback, useEffect} from 'react';
import {Editor, type Element, type Node, Range, Transforms} from 'slate';
import {ReactEditor, type RenderElementProps, useSlate} from 'slate-react';

import {getSafeUrlWithoutXss} from '../../../util/report/urls';
import {
  checkURLPointToCurrentReport,
  getURLAndSection,
} from '../../../util/urls';
import * as S from './links.styles';

export interface Link extends Element {
  type: 'link';
  url: string;
}

export const isLink = (node: Node): node is Link => node.type === 'link';

export const EditorWithLinks = {
  insertLink(editor: Editor, url: string) {
    if (editor.selection) {
      EditorWithLinks.wrapLink(editor, url);
    }
  },

  isLinkActive(editor: Editor) {
    const [link] = Editor.nodes(editor, {match: n => isLink(n)});
    return !!link;
  },

  unwrapLink(editor: Editor) {
    Transforms.unwrapNodes(editor, {match: n => isLink(n)});
  },

  wrapLink(editor: Editor, url: string) {
    if (EditorWithLinks.isLinkActive(editor)) {
      EditorWithLinks.unwrapLink(editor);
    }

    const {selection} = editor;
    const isCollapsed = selection && Range.isCollapsed(selection);
    const link = {
      type: 'link',
      url,
      children: isCollapsed ? [{text: url}] : [],
    };

    if (isCollapsed) {
      Transforms.insertNodes(editor, link);
    } else {
      Transforms.wrapNodes(editor, link, {split: true});
      Transforms.collapse(editor, {edge: 'end'});
    }
  },
};

export const LinkElement: React.FC<RenderElementProps & {element: Link}> = ({
  attributes,
  element,
  children,
}) => {
  let href = getSafeUrlWithoutXss(element.url);
  const linkToCurrentReport = checkURLPointToCurrentReport(href);
  let target = '_blank';

  if (linkToCurrentReport) {
    href = getURLAndSection(href).section ?? '';
    target = '_self';
  } else if (href.startsWith('#')) {
    // Hash links should navigate within the same page
    target = '_self';
  }

  return (
    <S.Link {...attributes} href={href} target={target}>
      {children}
    </S.Link>
  );
};

export const withLinks = <T extends Editor>(editor: T) => {
  const {insertText, isInline, insertBreak} = editor;

  editor.isInline = element => {
    return isLink(element) ? true : isInline(element);
  };

  editor.insertText = text => {
    if (text && isUrl(text)) {
      EditorWithLinks.wrapLink(editor, text);
    } else {
      insertText(text);
    }
  };

  editor.insertBreak = () => {
    const {selection} = editor;

    const linkEntry = Editor.above(editor, {match: n => isLink(n)});

    // when splitting the link, make the new text not linked
    if (selection && linkEntry) {
      Transforms.splitNodes(editor, {always: true});
      EditorWithLinks.unwrapLink(editor);

      const pointAfter = Editor.after(editor, editor.selection!);
      Transforms.select(editor, pointAfter!);

      return;
    }

    insertBreak();
  };

  return editor;
};

export const linkNodeToLatex = (node: Link, inner: string) => {
  return `\\href{${node.url}}{${inner}}`;
};

interface InsertLinkWidgetProps {
  savedSelection: Range;
  onCancel(): void;
  onClose(): void;
}

export const InsertLinkWidget: React.FC<InsertLinkWidgetProps> = ({
  savedSelection,
  onCancel,
  onClose,
}) => {
  const editor = useSlate();

  return (
    <S.LinkInput
      placeholder="Enter link URL"
      autoFocus
      onKeyDown={e => {
        if (e.key === 'Escape') {
          e.preventDefault();
          onCancel();
        }
        if (e.key === 'Enter') {
          Transforms.select(editor, savedSelection);
          ReactEditor.focus(editor);
          if (e.currentTarget.value.trim() !== '') {
            EditorWithLinks.wrapLink(editor, fixUrl(e.currentTarget.value));
          }
          Transforms.collapse(editor, {edge: 'end'});

          // Move the selection one position forward to exit the link
          const pointAfter = Editor.after(editor, editor.selection!);
          if (pointAfter) {
            Transforms.select(editor, pointAfter);
          }

          e.preventDefault();
          onClose();
        }
      }}></S.LinkInput>
  );
};

const fixUrl = (url: string) => {
  if (
    !url.startsWith('http://') &&
    !url.startsWith('https://') &&
    !url.startsWith('#')
  ) {
    url = 'http://' + url;
  }

  return url;
};

interface EditLinkWidgetProps {
  linkNode: Link;
  direction: 'up' | 'down';
  onClose(): void;
}

export const EditLinkWidget: React.FC<EditLinkWidgetProps> = ({
  onClose,
  linkNode,
  direction,
}) => {
  const editor = useSlate();
  const [tempUrl, setTempUrl] = React.useState(linkNode.url);
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const [linkDOMNode, setLinkDOMNode] = React.useState<HTMLElement | null>(
    null
  );
  const [focused, setFocused] = React.useState(false);

  useEffect(() => {
    setTempUrl(linkNode.url);
  }, [linkNode.url]);

  useEffect(() => {
    try {
      setLinkDOMNode(ReactEditor.toDOMNode(editor, linkNode));
    } catch (e) {
      console.error('Error getting link DOM node', e);
      setLinkDOMNode(null);
    }
  }, [editor, linkNode]);

  const onMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!linkDOMNode) {
        onClose();
        return;
      }

      const wrapperDOMRect = wrapperRef.current?.getBoundingClientRect();
      if (focused || !wrapperDOMRect) {
        return;
      }
      if (
        wrapperDOMRect &&
        e.x >= wrapperDOMRect.left &&
        e.x <= wrapperDOMRect.right &&
        e.y >= wrapperDOMRect.top &&
        e.y <= wrapperDOMRect.bottom
      ) {
        return;
      }

      const linkDOMRect = linkDOMNode.getBoundingClientRect();
      if (
        e.x >= linkDOMRect.left &&
        e.x <= linkDOMRect.right &&
        e.y >= linkDOMRect.top - (direction === 'up' ? 6 : 0) &&
        e.y <= linkDOMRect.bottom + (direction === 'down' ? 6 : 0)
      ) {
        return;
      }

      onClose();
    },
    [direction, focused, onClose, linkDOMNode]
  );

  useEffect(() => {
    const editorDOMNode = ReactEditor.toDOMNode(editor, editor);

    editorDOMNode.addEventListener('mousemove', onMouseMove);

    return () => {
      editorDOMNode.removeEventListener('mousemove', onMouseMove);
    };
  }, [editor, onMouseMove]);

  return (
    <S.EditLinkWrapper ref={wrapperRef}>
      <S.LinkInput
        placeholder="Enter link URL"
        value={tempUrl}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onChange={({target}) => setTempUrl(target.value)}
        onKeyDown={e => {
          if (e.key === 'Escape') {
            onClose();
          }
          if (e.key === 'Enter') {
            e.preventDefault();
            // Remove link when empty
            const trimmedUrl = e.currentTarget.value.trim();
            if (trimmedUrl === '') {
              Transforms.unwrapNodes(editor, {
                at: ReactEditor.findPath(editor, linkNode),
              });
              onClose();
              return;
            }

            // Fix up the URL so it can be parsed correctly
            const fixedUrl = fixUrl(trimmedUrl);
            Transforms.setNodes(
              editor,
              {url: fixedUrl},
              {at: ReactEditor.findPath(editor, linkNode)}
            );
            setTempUrl(fixedUrl);
            onClose();
          }

          e.stopPropagation();
        }}></S.LinkInput>
      <S.RemoveLinkButton
        name="close"
        onClick={() => {
          Transforms.unwrapNodes(editor, {
            at: ReactEditor.findPath(editor, linkNode),
          });
          onClose();
        }}></S.RemoveLinkButton>
    </S.EditLinkWrapper>
  );
};
